import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Pencil,
  FilePlus2,
  UserPlus,
  Eye,
  Trash2,
  Ban,
  ShieldCheck,
  MessageCircle,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/project/integrations/supabase/client";
import { Shell, PageHeader } from "@/project/components/Shell";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/project/lib/auth";
import { settingsQuery } from "@/project/lib/queries";
import {
  applicationDocHtml,
  printHtmlDocument,
  shareByEmail,
  shareOnWhatsApp,
} from "@/project/lib/print-doc";
import { inr, shortDate, maskAadhaar } from "@/project/lib/format";
import {
  SadharmikForm,
  saveSadharmik,
  validateSadharmik,
  loadSadharmik,
  type SadharmikValues,
} from "@/project/components/SadharmikForm";

export const Route = createFileRoute("/members")({
  head: () => ({
    meta: [
      { title: "Member Master · Assistance & Letter Desk" },
      {
        name: "description",
        content:
          "Master list of sadharmik member households with their family members, contact details and lifetime assistance received.",
      },
      { property: "og:title", content: "Member Master" },
      {
        property: "og:description",
        content:
          "Member households with family member details and assistance history.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: MembersPage,
});

function MembersPage() {
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState<SadharmikValues | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [blacklistId, setBlacklistId] = useState<string | null>(null);
  const [blacklistReason, setBlacklistReason] = useState("");
  const [editBlacklisted, setEditBlacklisted] = useState(false);
  const [editBlacklistReason, setEditBlacklistReason] = useState("");
  const { user } = useAuth();
  const qc = useQueryClient();
  const router = useRouter();
  const { data: settings } = useQuery(settingsQuery);

  const { data } = useQuery({
    queryKey: ["member-master"],
    queryFn: async () => {
      const [{ data: families }, { data: members }, { data: apps }] =
        await Promise.all([
          supabase
            .from("families")
            .select("*")
            .order("created_at", { ascending: false })
            .limit(500),
          supabase.from("family_members").select("*").order("created_at"),
          supabase
            .from("applications")
            .select("*, assistance_categories(name)")
            .order("application_date", { ascending: false }),
        ]);
      return {
        families: families ?? [],
        members: members ?? [],
        apps: apps ?? [],
      };
    },
  });

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    const membersByFamily = new Map<
      string,
      typeof data extends never ? never : any[]
    >();
    for (const m of data?.members ?? []) {
      const list = membersByFamily.get(m.family_id) ?? [];
      list.push(m);
      membersByFamily.set(m.family_id, list);
    }
    const stats = new Map<
      string,
      { count: number; total: number; last: string; latest: any }
    >();
    for (const a of data?.apps ?? []) {
      const key = a.family_id ?? a.ration_card_no ?? "";
      const prev = stats.get(key) ?? {
        count: 0,
        total: 0,
        last: "",
        latest: null,
      };
      const isNewer = !prev.last || prev.last < a.application_date;
      stats.set(key, {
        count: prev.count + 1,
        total: prev.total + Number(a.our_contribution ?? 0),
        last: isNewer ? a.application_date : prev.last,
        latest: isNewer ? a : prev.latest,
      });
    }
    return (data?.families ?? [])
      .map(f => {
        const members = membersByFamily.get(f.id) ?? [];
        return {
          ...f,
          members,
          head: members[0]?.full_name ?? "—",
          stats: stats.get(f.id) ?? stats.get(f.ration_card_no),
        };
      })
      .filter(f =>
        !term
          ? true
          : [
              f.ration_card_no,
              f.address,
              f.area,
              f.primary_mobile,
              ...f.members.flatMap((m: any) => [m.full_name, m.mobile]),
            ]
              .filter(Boolean)
              .some(v => String(v).toLowerCase().includes(term))
      );
  }, [data, q]);

  const open = rows.find(r => r.id === openId) ?? null;
  const totalMembers = rows.reduce((s, r) => s + r.members.length, 0);

  const startEdit = async (familyId: string) => {
    setBusy(true);
    try {
      setDraft(await loadSadharmik(familyId));
      const fam = (data?.families ?? []).find(
        (x: any) => x.id === familyId
      ) as any;
      setEditBlacklisted(Boolean(fam?.blacklisted));
      setEditBlacklistReason(fam?.blacklist_reason ?? "");
      setEditId(familyId);
      setOpenId(null);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!draft) {
      return;
    }
    const err = validateSadharmik(draft);
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      await saveSadharmik(draft, user?.id);
      if (editId) {
        const prev = (data?.families ?? []).find(
          (x: any) => x.id === editId
        ) as any;
        const changed =
          Boolean(prev?.blacklisted) !== editBlacklisted ||
          (prev?.blacklist_reason ?? "") !== editBlacklistReason;
        if (changed) {
          const { error } = await supabase
            .from("families")
            .update(
              editBlacklisted
                ? {
                    blacklisted: true,
                    blacklist_reason: editBlacklistReason || null,
                    blacklisted_at:
                      prev?.blacklisted_at ?? new Date().toISOString(),
                  }
                : {
                    blacklisted: false,
                    blacklist_reason: null,
                    blacklisted_at: null,
                  }
            )
            .eq("id", editId);
          if (error) {
            throw error;
          }
        }
      }
      toast.success("Member household updated");
      setEditId(null);
      setDraft(null);
      qc.invalidateQueries({ queryKey: ["member-master"] });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const shareText = (f: any) => {
    const a = f.stats?.latest;
    const lines = [
      `*${settings?.["org_heading"] ?? "Sadharmik Assistance Desk"}*`,
      `Member: ${f.head}`,
      f.primary_mobile ? `Mobile: ${f.primary_mobile}` : "",
      f.ration_card_no ? `Ration Card: ${f.ration_card_no}` : "",
      a
        ? `Application Ref: ${a.ref_no} (${shortDate(a.application_date)})`
        : "",
      a ? `Category: ${a.assistance_categories?.name ?? "—"}` : "",
      a ? `Total requirement: ${inr(a.total_requirement)}` : "",
      a ? `Balance required: ${inr(a.balance_required)}` : "",
      "",
      "The application PDF is attached for your kind consideration.",
    ];
    return lines.filter(Boolean).join("\n");
  };

  const buildPdf = (f: any) => {
    const a = f.stats?.latest;
    if (!a) {
      toast.error("No application on record for this member yet.");
      return false;
    }
    printHtmlDocument(
      `Application-${a.ref_no}`,
      applicationDocHtml(
        { ...a, categoryName: a.assistance_categories?.name },
        settings ?? {}
      )
    );
    return true;
  };

  const whatsappShare = (f: any) => {
    if (!buildPdf(f)) {
      return;
    }
    toast.info(
      "Save the PDF from the print dialog, then attach it in WhatsApp."
    );
    shareOnWhatsApp(f.primary_mobile, shareText(f));
  };

  const emailShare = (f: any) => {
    if (!buildPdf(f)) {
      return;
    }
    shareByEmail(
      "",
      `Assistance application — ${f.head}`,
      shareText(f).replace(/\*/g, "")
    );
  };

  const toggleBlacklist = async (f: any) => {
    if (f.blacklisted) {
      const { error } = await supabase
        .from("families")
        .update({
          blacklisted: false,
          blacklist_reason: null,
          blacklisted_at: null,
        })
        .eq("id", f.id);
      if (error) {
        toast.error(error.message);
        return;
      }
      toast.success("Member removed from blacklist");
      qc.invalidateQueries({ queryKey: ["member-master"] });
    } else {
      setBlacklistReason("");
      setBlacklistId(f.id);
    }
  };

  const confirmBlacklist = async () => {
    if (!blacklistId) {
      return;
    }
    const { error } = await supabase
      .from("families")
      .update({
        blacklisted: true,
        blacklist_reason: blacklistReason || null,
        blacklisted_at: new Date().toISOString(),
      })
      .eq("id", blacklistId);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Member blacklisted");
    setBlacklistId(null);
    qc.invalidateQueries({ queryKey: ["member-master"] });
  };

  return (
    <Shell>
      <PageHeader
        title="Member Master"
        subtitle={`${rows.length} member household(s) · ${totalMembers} family member(s) on record`}
        actions={
          <Button onClick={() => router.navigate({ to: "/applications/new" })}>
            <UserPlus className="mr-2 h-4 w-4" /> Add Member Household
          </Button>
        }
      />

      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search by member name, ration card, address, area or mobile…"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="data-table w-full text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Head of Family</th>
                <th className="px-4 py-3">Ration Card</th>
                <th className="hidden px-4 py-3 2xl:table-cell">
                  Address / Area
                </th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Members</th>
                <th className="px-4 py-3 text-right">Applications</th>
                <th className="hidden px-4 py-3 text-right xl:table-cell">
                  Assistance Given
                </th>
                <th className="hidden px-4 py-3 xl:table-cell">
                  Last Application
                </th>
                <th className="sticky right-0 z-10 bg-muted px-2 py-3 text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(f => (
                <tr
                  key={f.id}
                  className={
                    f.blacklisted
                      ? "bg-destructive/5 hover:bg-destructive/10 border-t border-border"
                      : "hover:bg-muted/40 border-t border-border"
                  }
                >
                  <td className="px-4 py-2 font-medium">{f.head}</td>
                  <td className="px-4 py-2">{f.ration_card_no}</td>
                  <td className="hidden max-w-[180px] px-4 py-2 2xl:table-cell">
                    <span className="block truncate">{f.address ?? "—"}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {f.area ?? ""}
                    </span>
                  </td>
                  <td className="px-4 py-2">{f.primary_mobile ?? "—"}</td>
                  <td className="px-4 py-2">
                    {f.blacklisted ? (
                      <Badge
                        variant="destructive"
                        className="gap-1"
                        title={f.blacklist_reason ?? "Blacklisted"}
                      >
                        <Ban className="h-3.5 w-3.5" /> Blacklisted
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1">
                        <ShieldCheck className="h-3.5 w-3.5" /> Active
                      </Badge>
                    )}
                  </td>

                  <td className="px-4 py-2 text-right">{f.members.length}</td>
                  <td className="px-4 py-2 text-right">
                    {f.stats?.count ?? 0}
                  </td>
                  <td className="hidden px-4 py-2 text-right xl:table-cell">
                    {inr(f.stats?.total ?? 0)}
                  </td>
                  <td className="hidden px-4 py-2 xl:table-cell">
                    {shortDate(f.stats?.last)}
                  </td>
                  <td className="sticky right-0 z-10 whitespace-nowrap bg-background px-2 py-2 text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      title="View household & members"
                      aria-label={`View ${f.head}`}
                      onClick={() => setOpenId(f.id)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Edit household & members"
                      aria-label={`Edit ${f.head}`}
                      disabled={busy}
                      onClick={() => startEdit(f.id)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Share application PDF on WhatsApp"
                      aria-label={`Share ${f.head} on WhatsApp`}
                      onClick={() => whatsappShare(f)}
                    >
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Share application PDF by email"
                      aria-label={`Email ${f.head}`}
                      onClick={() => emailShare(f)}
                    >
                      <Mail className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      title={
                        f.blacklisted
                          ? "Remove from blacklist"
                          : "Blacklist member"
                      }
                      aria-label={`Blacklist ${f.head}`}
                      className={
                        f.blacklisted ? "text-primary" : "text-destructive"
                      }
                      onClick={() => toggleBlacklist(f)}
                    >
                      {f.blacklisted ? (
                        <ShieldCheck className="h-4 w-4" />
                      ) : (
                        <Ban className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Delete household"
                      aria-label={`Delete ${f.head}`}
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteId(f.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td
                    colSpan={10}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    No members recorded yet. Start from{" "}
                    <Link to="/applications/new" className="underline">
                      New Assistance
                    </Link>
                    .
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={Boolean(open)} onOpenChange={o => !o && setOpenId(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {open?.head} — {open?.ration_card_no}
            </DialogTitle>
          </DialogHeader>
          {open && (
            <div className="space-y-4 text-sm">
              <div className="grid gap-3 md:grid-cols-3">
                <Info label="Mobile" value={open.primary_mobile ?? "—"} />
                <Info label="Alternate" value={open.alt_mobile ?? "—"} />
                <Info
                  label="Area / City"
                  value={
                    [open.area, open.city].filter(Boolean).join(", ") || "—"
                  }
                />
                <div className="md:col-span-3">
                  <Info label="Address" value={open.address ?? "—"} />
                </div>
                <Info
                  label="Applications"
                  value={String(open.stats?.count ?? 0)}
                />
                <Info
                  label="Assistance Given"
                  value={inr(open.stats?.total ?? 0)}
                />
                <Info
                  label="Last Application"
                  value={shortDate(open.stats?.last)}
                />
              </div>

              <div className="overflow-x-auto rounded-md border border-border">
                <table className="data-table w-full text-sm">
                  <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Relation</th>
                      <th className="px-3 py-2">Gender</th>
                      <th className="px-3 py-2">Age</th>
                      <th className="px-3 py-2">Mobile</th>
                      <th className="px-3 py-2">Aadhaar</th>
                      <th className="px-3 py-2">Occupation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {open.members.map((m: any, i: number) => (
                      <tr key={m.id ?? i} className="border-t border-border">
                        <td className="px-3 py-2 font-medium">
                          {m.full_name}
                          {i === 0 && (
                            <Badge variant="secondary" className="ml-2">
                              Head
                            </Badge>
                          )}
                        </td>
                        <td className="px-3 py-2">{m.relation ?? "—"}</td>
                        <td className="px-3 py-2">{m.gender ?? "—"}</td>
                        <td className="px-3 py-2">{m.age ?? "—"}</td>
                        <td className="px-3 py-2">{m.mobile ?? "—"}</td>
                        <td className="px-3 py-2">{maskAadhaar(m.aadhaar)}</td>
                        <td className="px-3 py-2">{m.occupation ?? "—"}</td>
                      </tr>
                    ))}
                    {!open.members.length && (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-3 py-6 text-center text-muted-foreground"
                        >
                          No family members captured for this household.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button disabled={busy} onClick={() => startEdit(open.id)}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit Household &amp;
                  Members
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    router.navigate({
                      to: "/applications/new",
                      search: { family: open.id },
                    })
                  }
                >
                  <FilePlus2 className="mr-2 h-4 w-4" /> New Application for
                  this Member
                </Button>
                <Button variant="outline" onClick={() => whatsappShare(open)}>
                  <MessageCircle className="mr-2 h-4 w-4" /> Share on WhatsApp
                </Button>
                <Button variant="outline" onClick={() => emailShare(open)}>
                  <Mail className="mr-2 h-4 w-4" /> Share by Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={Boolean(deleteId)}
        onOpenChange={o => !o && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this member household?</AlertDialogTitle>
            <AlertDialogDescription>
              All family members captured for this household will be removed.
              Households that already have applications on record cannot be
              deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={async e => {
                e.preventDefault();
                if (!deleteId) {
                  return;
                }
                setBusy(true);
                try {
                  const { count } = await supabase
                    .from("applications")
                    .select("id", { count: "exact", head: true })
                    .eq("family_id", deleteId);
                  if (count && count > 0) {
                    toast.error(
                      `This household has ${count} application(s). Delete those first.`
                    );
                    return;
                  }
                  await supabase
                    .from("family_members")
                    .delete()
                    .eq("family_id", deleteId);
                  const { error } = await supabase
                    .from("families")
                    .delete()
                    .eq("id", deleteId);
                  if (error) {
                    throw error;
                  }
                  toast.success("Member household deleted");
                  setDeleteId(null);
                  qc.invalidateQueries({ queryKey: ["member-master"] });
                } catch (err) {
                  toast.error((err as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={Boolean(blacklistId)}
        onOpenChange={o => !o && setBlacklistId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Blacklist this member?</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase">Reason (optional)</Label>
            <Textarea
              value={blacklistReason}
              onChange={e => setBlacklistReason(e.target.value)}
              placeholder="e.g. Repeated misuse of assistance / incorrect information"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlacklistId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmBlacklist}>
              Blacklist Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(editId && draft)}
        onOpenChange={o => {
          if (!o) {
            setEditId(null);
            setDraft(null);
          }
        }}
      >
        <DialogContent className="max-h-[88vh] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Member Household</DialogTitle>
          </DialogHeader>
          {draft && <SadharmikForm value={draft} onChange={setDraft} />}
          {draft && (
            <div className="space-y-3 rounded-md border border-border p-4">
              <Label className="text-xs uppercase">Blacklist Member</Label>
              <RadioGroup
                value={editBlacklisted ? "yes" : "no"}
                onValueChange={v => setEditBlacklisted(v === "yes")}
                className="flex gap-6"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="no" id="bl-no" />
                  <Label
                    htmlFor="bl-no"
                    className="flex items-center gap-1 font-normal"
                  >
                    <ShieldCheck className="h-4 w-4 text-primary" /> Active
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="yes" id="bl-yes" />
                  <Label
                    htmlFor="bl-yes"
                    className="flex items-center gap-1 font-normal"
                  >
                    <Ban className="h-4 w-4 text-destructive" /> Blacklisted
                  </Label>
                </div>
              </RadioGroup>
              {editBlacklisted && (
                <Textarea
                  value={editBlacklistReason}
                  onChange={e => setEditBlacklistReason(e.target.value)}
                  placeholder="Reason for blacklisting (optional)"
                />
              )}
            </div>
          )}
          <DialogFooter>
            <Button disabled={busy} onClick={saveEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="font-medium">{value}</p>
    </div>
  );
}
